import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import {
    useListAgentsQuery,
    useListHospitalsQuery,
    useListOrgTokensQuery,
    useListProtocolsQuery,
} from '@/api/hubApi';
import { StatTile } from '@/pages/StatTile';
import { TopHospitalStatTile } from '@/pages/TopHospitalStatTile';

// Scans still in flight — queued at the Scan click or running against ETL —
// as opposed to one that finished (completed/completed_with_errors) or never
// started at all.
const PENDING_SCAN_STATUSES = new Set(['queued', 'running']);

export function DashboardPage() {
    const agents = useListAgentsQuery();
    const hospitals = useListHospitalsQuery();
    const orgTokens = useListOrgTokensQuery();
    const protocols = useListProtocolsQuery();

    // Every sponsor is created through an org-token mint (there is no other
    // creation path — see orgToken.service.js), so the sponsor-typed rows here
    // already cover every sponsor. Deduped by org id: a sponsor that revoked
    // and re-minted its token would otherwise appear twice.
    const sponsorsCount = orgTokens.data
        ? new Set(
              orgTokens.data.filter((row) => row.org_type === 'sponsor').map((row) => row.org.id),
          ).size
        : undefined;
    const scannedProtocolsCount = protocols.data?.filter((p) => p.scanned).length;
    const scannedPendingProtocolsCount = protocols.data?.filter(
        (p) => p.latest_scan_status && PENDING_SCAN_STATUSES.has(p.latest_scan_status),
    ).length;

    // Summed from each agent's own scan_count (GET /api/v1/agents) rather than
    // a separate backend aggregate — an agent belongs to exactly one hospital,
    // so grouping client-side is the same number a server-side GROUP BY would
    // give, without a dedicated endpoint for one dashboard tile.
    let topHospital: { name: string; count: number } | undefined;
    if (agents.data && hospitals.data) {
        const scansByHospital = new Map<string, number>();
        for (const agent of agents.data) {
            scansByHospital.set(
                agent.hospital_id,
                (scansByHospital.get(agent.hospital_id) ?? 0) + agent.scan_count,
            );
        }
        let topHospitalId: string | undefined;
        let topCount = 0;
        for (const [hospitalId, count] of scansByHospital) {
            if (count > topCount) {
                topHospitalId = hospitalId;
                topCount = count;
            }
        }
        const hospital = hospitals.data.find((h) => h.id === topHospitalId);
        if (hospital) topHospital = { name: hospital.name, count: topCount };
    }

    return (
        <Box>
            <Typography variant="h5" sx={{ mb: 2 }}>
                Dashboard
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <TopHospitalStatTile
                    name={topHospital?.name}
                    scanCount={topHospital?.count}
                    isLoading={agents.isLoading || hospitals.isLoading}
                    isError={!!agents.error || !!hospitals.error}
                />
                <StatTile
                    label="Agents"
                    value={agents.data?.length}
                    isLoading={agents.isLoading}
                    isError={!!agents.error}
                />
                <StatTile
                    label="Sponsors"
                    value={sponsorsCount}
                    isLoading={orgTokens.isLoading}
                    isError={!!orgTokens.error}
                />
                <StatTile
                    label="Scanned protocols"
                    value={scannedProtocolsCount}
                    isLoading={protocols.isLoading}
                    isError={!!protocols.error}
                />
                <StatTile
                    label="Scanned protocols pending"
                    value={scannedPendingProtocolsCount}
                    isLoading={protocols.isLoading}
                    isError={!!protocols.error}
                />
            </Box>
        </Box>
    );
}
